import {ReviewSummary} from 'constants/SourceConstants';
import {QueryResultType, SkippableBenchlingQueryResult} from 'graphql-client/BenchlingQuery';

import {HomepageV2AssignedProcessTasksQuery} from '../__generated/HomepageV2AssignedProcessTasksQuery';
import {
  ReviewItemStatusLabel,
  REVIEW_STATUS_LABEL_TO_SUMMARY_KEY,
  TaskStatusLabel,
  TASK_STATUS_LABEL_TO_SUMMARY_KEY,
  TASK_STATUS_TYPE_TO_SUMMARY_KEY,
  TasksReviewSummaryCountKey,
} from './MyWorkConstants';

export type TasksReviewSummary = Record<TasksReviewSummaryCountKey, number> & {
  ids: Record<TasksReviewSummaryCountKey, Array<string>>;
  statusIds: Record<TasksReviewSummaryCountKey, Array<string>>;
};

function createEmptyTasksReviewSummary(): TasksReviewSummary {
  return {
    cancelled: 0,
    completed: 0,
    failed: 0,
    invalid: 0,
    inProgress: 0,
    pending: 0,
    skipped: 0,
    ids: {
      cancelled: [],
      completed: [],
      failed: [],
      invalid: [],
      inProgress: [],
      pending: [],
      skipped: [],
    },
    statusIds: {
      cancelled: [],
      completed: [],
      failed: [],
      invalid: [],
      inProgress: [],
      pending: [],
      skipped: [],
    },
  };
}

export function getSelectedReviewSummaryIds(
  summary: ReviewSummary | null,
  selectedStatusLabels: Array<string>
): Array<string> | null {
  // null until the entry/worksheet review summary pre-query has loaded.
  if (summary == null) {
    return null;
  }
  return selectedStatusLabels.flatMap((statusLabel) => {
    const summaryKey = REVIEW_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel] as keyof ReviewSummary['ids'];
    return summary.ids[summaryKey];
  });
}

export function getReviewStatusCount(
  summary: ReviewSummary | null,
  statusLabel: ReviewItemStatusLabel
): number {
  if (summary == null) {
    return 0;
  }
  return summary[REVIEW_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel]] ?? 0;
}

export function makeTasksReviewSummary(
  tasksQueryResult: SkippableBenchlingQueryResult<HomepageV2AssignedProcessTasksQuery>
): TasksReviewSummary | null {
  if (tasksQueryResult.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const summary = createEmptyTasksReviewSummary();
  for (const curr of tasksQueryResult.data.assignedProcessTaskStatusSummaries) {
    const statusKey = TASK_STATUS_TYPE_TO_SUMMARY_KEY[curr.status.statusType];
    summary[statusKey] += curr.count;
    summary.ids[statusKey] = [...summary.ids[statusKey], ...curr.taskIds];
    summary.statusIds[statusKey] = [...summary.statusIds[statusKey], curr.status.id];
  }
  return summary;
}

export function getSelectedTaskIds(
  tasksReviewSummary: TasksReviewSummary | null,
  selectedStatusLabels: Array<string>
): Array<string> | null {
  // null until the assigned process task summary pre-query has loaded.
  if (tasksReviewSummary == null) {
    return null;
  }
  return selectedStatusLabels.flatMap(
    (statusLabel) => tasksReviewSummary.ids[TASK_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel]]
  );
}

export function getTaskStatusCount(
  tasksReviewSummary: TasksReviewSummary | null,
  statusLabel: TaskStatusLabel
): number {
  if (tasksReviewSummary == null) {
    return 0;
  }
  return tasksReviewSummary[TASK_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel]];
}

export function getTaskStatusIds(
  tasksReviewSummary: TasksReviewSummary | null,
  statusLabel: TaskStatusLabel
): Array<string> | null {
  if (tasksReviewSummary == null) {
    return null;
  }
  return tasksReviewSummary.statusIds[TASK_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel]];
}
